/* eslint-disable max-len */
/* eslint-disable complexity */
/**
 * @file 任务详情配置
 * @author sunyingying01
 */

import isEqual from 'lodash/isEqual';
import {makeObservable, observable, action, flow, computed} from 'mobx';
import {isEmpty} from 'lodash';
import {expertUpload, deleteFileApi} from '@/api/upload';
import {UploadFileInfo} from '@/stores/jobs/UploadFileInfo';

/** 任务详情配置store */
export class DescParameterStore {
    static MAX_FILE_COUNT = 5;

    static EVOLVE_CONFIG_VAL: ParameterTypes = {
        name: '',
        max_iterations: 50,
        population_size: 50,
        num_workers: 4,
        num_islands: 4,
        enable_cold_start: false
    };

    static EXAMPLE_TXT = {
        background: [
            {
                title: '示例一：工业智能信控',
                content: '选择「深度精细优化（聚焦局部最优）」或「广度多样探索（避免局部最优）」，也可指定「速度优先」或「精度优先」；\n参考知识：调度优化常见算法（如遗传算法、禁忌搜索、模拟退火）、Job-Shop Scheduling 的经典研究。'
            },
            {
                title: '示例二：金融风险评估',
                content: '算法应用于量化交易的风险管理，主要挑战在于同时评估大规模组合的风险暴露和市场波动对模型稳定性的影响。\n参考知识：VaR（在险价值）、CVaR（条件在险价值）、蒙特卡洛模拟、金融时间序列分析。'
            },
            {
                title: '示例三：AI算子优化',
                description: '明确不同目标 / 指标间的取舍规则，如「更快收敛 vs 解的多样性」「运行时间 vs 精度」',
                content: '算法应用于量化交易的风险管理，主要挑战在于同时评估大规模组合的风险暴露和市场波动对模型稳定性的影响。\n参考知识：高性能计算（HPC）、并行计算框架（MPI/CUDA）、多目标优化方法。'
            }
        ],
        strategy: [
            {
                title: '方向一：演化方向',
                description: '选择「深度精细优化（聚焦局部最优）」或「广度多样探索（避免局部最优）」，也可指定「速度优先」或「精度优先」；',
                content: '希望算法在演化过程中更多探索不同调度顺序（广度），以避免陷入局部最优；在后期再逐渐收敛到更优解（深度）。优先目标是整体任务完成时间，其次是资源利用率，最后是鲁棒性。'
            },
            {
                title: '方向二：优化优先级',
                description: '例如「运行效率＞结果稳定性＞多样性」「风险控制优先于收益最大化」',
                content: '期望算法在早期偏向广度搜索（覆盖更多组合），中期在风险敏感性上进行深度优化，后期则平衡收益与波动。优化优先级为：风险稳定性 > 收益增长 > 计算开销。'
            },
            {
                title: '方向三：多目标权衡',
                description: '明确不同目标 / 指标间的取舍规则，如「更快收敛 vs 解的多样性」「运行时间 vs 精度」',
                content: '希望在演化过程中兼顾探索不同参数组合的多样性（广度），同时逐步加深对关键参数的精细优化（深度）。优先保证模拟精度，其次追求计算效率，再考虑结果的可扩展性。'
            }
        ]
    };
    /** 最大字数限制与允许字符正则 */
    static MAX_PROMPT_LENGTH = 10000;
    /**
     * 允许内容：
     * - 中文、英文、数字
     * - 空格、换行
     * - 常见标点：中英文逗号、句号、顿号、引号、括号、书名号、分号、冒号、问号、感叹号、减号、斜杠等
     */
    static ALLOWED_PROMPT_REGEX = /^[\u4e00-\u9fa5a-zA-Z0-9\s\n，。、；;：“”‘’"'\(\)\（\）【】\[\]{}《》<>\?？!！:：,.\-_/\\|@#$%^&*+=~`]+$/;

    /** 任务id */
    job_id: string = '';
    /** 开启任务加载状态 */
    fetchLoading: boolean = false;
    /** 开启任务错误信息 */
    startTaskErrorMsg: string = '';

    /** 专家库文件列表 */
    expert_lib: UploadFileInfo[] = [];

    /** 运行界面默认最大迭代次数 */
    defaultMin_iterations: number = 50;

    evolveConfig: ParameterTypes = DescParameterStore.EVOLVE_CONFIG_VAL;
    /** 任务演化阶段重跑配置 */
    reEvolveConfig: ParameterTypes = DescParameterStore.EVOLVE_CONFIG_VAL;

    prompt: PromptTypes = {
        task: '',
        background: '',
        strategy: ''
    };

    // evolveConfig 校验error信息
    coldstart_max_iterations_msg: string = '';
    max_iterations_msg: string = '';
    population_size_msg: string = '';
    num_workers_msg: string = '';
    num_islands_msg: string = '';
    name_msg: string = '';

    // prompt 校验error信息
    task_msg: string = '';
    background_msg: string = '';
    strategy_msg: string = '';

    initialSnapshot: any = null;

    constructor(job_id: string) {
        this.job_id = job_id;
        makeObservable(this, {
            job_id: observable,
            fetchLoading: observable,
            startTaskErrorMsg: observable,
            expert_lib: observable,
            evolveConfig: observable,
            prompt: observable,
            defaultMin_iterations: observable,
            initialSnapshot: observable,
            reEvolveConfig: observable,
            nameTouched: observable,
            setReEvolveConfig: action.bound,
            updateEvolveConfig: action.bound,
            updatePrompt: action.bound,
            integerValidate: action.bound,
            numberValidate: action.bound,
            startTaskValidate: action.bound,
            uploadFileValidate: action.bound,
            updateExpertLibFile: action.bound,
            updateStartTaskErrorMsg: action.bound,
            updateFetchLoading: action.bound,
            restoreDescParameterFormBackend: action.bound,
            clearPromptFieldError: action.bound,
            sanitizePromptValue: action.bound,
            fetchUploadExpert: flow.bound,
            fetchDeleteExpert: flow.bound,
            hasChanged: computed,
            isMaxReached: computed,
            resetSnapshot: action.bound
        });
    }

    /** 是否更改 */
    get hasChanged() {
        if (!this.initialSnapshot) {
            return false;
        }
        return !isEqual(this.initialSnapshot, this.toJSON());
    }

    get isMaxReached() {
        return this.expert_lib.length >= DescParameterStore.MAX_FILE_COUNT;
    }

    * fetchUploadExpert(this: DescParameterStore, uploadFile: File) {
        const fileInfo: UploadFileInfo = new UploadFileInfo();
        try {
            fileInfo.originalFile = uploadFile;
            const formData = new FormData();
            [uploadFile].forEach(file => {
                formData.append('expert_lib', file);
            });

            formData.append('job_id', this.job_id);

            const res: ApiResponse = yield expertUpload(formData);
            const resData = res.data || {};
            fileInfo.name = resData.file_name;
            fileInfo.fileUrl = resData.bos_url;
            this.updateExpertLibFile(fileInfo);
        }
        catch (error) {
            fileInfo.status = 'error';
            fileInfo.uploadErrorMessage = error instanceof Error ? error.message : '文件上传失败！';
            throw error;
        }
    }

    * fetchDeleteExpert(this: DescParameterStore, file: File) {
        yield deleteFileApi({
            job_id: this.job_id,
            file_name: file.name,
            tag: 'expert_lib',
            is_dir: false
        });
    }

    /** 更新专家资料文件上传list */
    updateExpertLibFile(file: UploadFileInfo) {
        this.expert_lib.push(file);
    }

    /** 开启任务错误信息 */
    updateStartTaskErrorMsg(msg: string) {
        this.startTaskErrorMsg = msg;
    }

    resetSnapshot() {
        this.initialSnapshot = JSON.parse(JSON.stringify(this.toJSON()));
    }


    /** 续跑参数配置 */
    setReEvolveConfig(params: Partial<ParameterTypes>) {
        Object.assign(this.reEvolveConfig, params);
    }

    /** 更新参数配置 */
    updateEvolveConfig(params: Partial<ParameterTypes>) {
        Object.assign(this.evolveConfig, params);
    }

    clearPromptFieldError(name: keyof PromptTypes) {
        switch (name) {
            case 'task':
                this.task_msg = '';
                break;
            case 'background':
                this.background_msg = '';
                break;
            case 'strategy':
                this.strategy_msg = '';
                break;
        }
    }

    /** 新增：清洗输入内容（去非法字符、截断超长） */
    sanitizePromptValue(value: string): string {
        if (!value) {
            return '';
        }

        // 分割为行后，对每行进行字符过滤，再拼回
        // 按行过滤，保留换行
        return value
            .split('\n')
            .map(line => {
                return Array.from(line)
                    .filter(char => DescParameterStore.ALLOWED_PROMPT_REGEX.test(char))
                    .join('');
            })
            .join('\n');
    }

    /** 更新prompt参数 带输入校验与清洗 */
    updatePrompt(params: Partial<PromptTypes>) {
        const sanitizedParams: Partial<PromptTypes> = {};

        if (params.task !== undefined) {
            sanitizedParams.task = this.sanitizePromptValue(params.task);
            this.task_msg = '';
        }
        if (params.background !== undefined) {
            sanitizedParams.background = this.sanitizePromptValue(params.background);
            this.background_msg = '';
        }
        if (params.strategy !== undefined) {
            sanitizedParams.strategy = this.sanitizePromptValue(params.strategy);
            this.strategy_msg = '';
        }

        Object.assign(this.prompt, sanitizedParams);
    }


    /** 校验单个prompt字段 */
    validatePromptField(fieldName: keyof PromptTypes): boolean {
        const value = this.prompt[fieldName];
        const errorField = `${fieldName}_msg` as keyof this;

        if (!value?.trim()) {
            (this[errorField] as string) = '内容不能为空';
            return false;
        }

        if (value.length > DescParameterStore.MAX_PROMPT_LENGTH) {
            (this[errorField] as string) = `内容长度不能超过 ${DescParameterStore.MAX_PROMPT_LENGTH} 字`;
            return false;
        }

        const hasInvalidChar = Array.from(value).some(
            char => !DescParameterStore.ALLOWED_PROMPT_REGEX.test(char)
        );
        if (hasInvalidChar) {
            (this[errorField] as string) = '内容包含非法字符，请检查输入';
            return false;
        }

        (this[errorField] as string) = '';
        return true;
    }

    /** 更新任务创建loading状态 */
    updateFetchLoading(loading: boolean) {
        this.fetchLoading = loading;
    }

    /** 数字校验 */
    numberValidate(name: keyof ParameterTypes): boolean {
        const value = this.evolveConfig[name];
        // 数字校验
        if (typeof value !== 'number' || isNaN(value)) {
            return false;
        }

        // 正数校验
        if (value < 0) {
            return false;
        }

        return true;
    }

    /** 名称是否已触发表单校验（避免刚进入页面就显示错误） */
    nameTouched: boolean = false;

    /** 续跑任务名称是否合法 */
    isReEvolveNameValid(): boolean {
        const value = this.reEvolveConfig.name;
        const pattern = /^[\u4e00-\u9fa5a-zA-Z0-9_]+$/;

        return Boolean(value?.trim() && pattern.test(value.trim()));
    }

    /** 名称校验 */
    nameValidate(): boolean {
        if (!this.nameTouched) {
            return true;
        }
        const value = this.reEvolveConfig.name;
        if (!value || !value.trim()) {
            this.name_msg = '名称不能为空';
            return false;
        }
        if (!this.isReEvolveNameValid()) {
            this.name_msg = '名称仅支持常用汉字、字母、数字和下划线';
            return false;
        }
        this.name_msg = '';
        return true;
    }

    /** 整型校验 */
    integerValidate(name: keyof ParameterTypes, messageName: keyof ErrorMsgType): boolean {
        const value = this.evolveConfig[name];
        if (!this.numberValidate(name)) {

            (this[messageName]) = '必须是有效的数字';
            return false;
        }

        if (!Number.isInteger(value)) {
            (this[messageName]) = '必须是整数';
            return false;
        }

        (this[messageName]) = '';
        return true;
    }

    /**
     * @description
     * 验证是否已经上传文件，如果不能则返回false，并设置错误信息，否则返回true
     *
     * @returns {boolean} true表示校验通过，false表示校验不通过，并设置错误信息
     */

    uploadFileValidate(): boolean {
        if (!this.expert_lib.length) {
            this.startTaskErrorMsg = '请上传文件！';
            return false;
        }

        this.startTaskErrorMsg = '';
        return true;
    }

    /** 校验prompt字段是否为空 */
    validatePrompt(): boolean {
        const fields: Array<keyof PromptTypes> = ['task', 'background', 'strategy'];
        let isValid = true;

        fields.forEach(field => {
            if (!this.validatePromptField(field)) {
                isValid = false;
            }
        });

        return isValid;
    }

    /**
     * 任务开启校验
     * @description 校验上传文件、prompt和参数配置
     * */
    startTaskValidate(): boolean {
        // 先校验prompt字段
        if (!this.validatePrompt()) {
            this.startTaskErrorMsg = '请完善prompt信息';
            return false;
        }
        const hasErrors = [
            'max_iterations_msg',
            'population_size_msg',
            'num_workers_msg',
            'num_islands_msg'
        ].some(field => Boolean(this[field as keyof typeof this]));

        this.startTaskErrorMsg = hasErrors ? '请检查参数配置' : '';

        if (hasErrors) {
            return false;
        }

        return true;
    }

    /** 后端数据整理成前端store */
    restoreDescParameterFormBackend(json: any): void {
        this.job_id = json.job_id;

        // 任务续跑获取详情
        // 如果 continue_evolve_config 存在且不为空，优先使用续跑配置
        if (json.continue_evolve_config && !isEmpty(json.continue_evolve_config.evolve_config)) {
            const {evolve_config} = json.continue_evolve_config;
            this.reEvolveConfig = evolve_config;
            this.reEvolveConfig.name = json.continue_evolve_config.name;
        }
        // 正常场景：使用原始的 evolve_config
        if (json.evolve_config && !isEmpty(json.evolve_config)) {
            this.evolveConfig = json.evolve_config || this.evolveConfig;
            this.defaultMin_iterations = json.evolve_config.max_iterations;
        }

        if (json.prompts) {
            this.prompt.background = json.prompts?.background ?? this.prompt.background;
            this.prompt.task = json.prompts?.task ?? this.prompt.task;
            this.prompt.strategy = json.prompts?.strategy ?? this.prompt.strategy;
        }

        this.expert_lib = (json.expert_lib || []).map((item: ExpertFileInfo) => {
            const fileInfo = new UploadFileInfo();
            fileInfo.name = item.file_name;
            fileInfo.fileUrl = item.bos_url;
            fileInfo.object_name = item.object_name;
            fileInfo.path = item.path;
            return fileInfo;
        });
    }

    toJSON() {
        return {
            prompt: this.prompt,
            evolveConfig: this.evolveConfig
        };
    }
}

export interface ParameterTypes {
    name: string;
    max_iterations: number;
    population_size: number;
    num_workers: number;
    num_islands: number;
    enable_cold_start: boolean;
}

export interface ErrorMsgType {
    coldstart_max_iterations_msg: string;
    max_iterations_msg: string;
    population_size_msg: string;
    num_workers_msg: string;
    num_islands_msg: string;
    name_msg: string;
}
